import { rem } from 'polished'
import React, { useContext, useRef } from 'react'
import clsx from 'clsx'
import styled from 'styled-components'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import GlobalMobileSearchHeroBar from 'components/SearchV2/Components/Mobile/GlobalMobileSearchHeroBar'
import HeroImage from 'content/components/HeroImage'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import SearchForm from 'components/Search/SearchForm'
import {
  HOTEL_SEARCH_TYPEAHEAD_TYPES,
  LOCATION_SEARCH_INPUT_PLACEHOLDER_LABEL,
  LOCATION_SEARCH_INPUT_PLACEHOLDER_LABEL_NATURAL_LANGUAGE,
  LOCATION_SEARCH_INPUT_TITLE_LABEL,
  LOCATION_SEARCH_INPUT_TITLE_LABEL_NATURAL_LANGUAGE,
} from 'constants/search'
import config from 'constants/config'
import { convertDatesToString, encodeSearchParams, isSearchEnabled } from 'lib/search/searchUtils'
import { connect } from 'react-redux'
import { useTrendingDestinationsLabels } from './hooks/userTrendingLabels'
import zIndex from 'styles/tools/z-index'
import useOptimizelyExperiment from '../../../../hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import { getOptimizelyEnabledDecision } from 'lib/optimizely/optimizelyUtils'
import HotelsTabContent from 'components/SearchV2/Components/Mobile/TabContents/HotelsTabContent'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { useAppDispatch } from 'hooks/reduxHooks'
import { pushWithRegion } from 'actions/NavigationActions'
import { useLocation } from 'react-router'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'

const HeroContainer = styled(HeroImage)`
  min-height: unset;
  padding: ${rem(40)} 0;

  &.expanded-mobile-search {
    padding-bottom: ${rem(140)};
  }

  ${mediaQueryUp.tablet} {
    min-height: ${rem(289)};
    padding-bottom: ${rem(40)};
  }
`

const HotelSearchContainerPaneContent = styled.div`
  padding: ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.default.eight};

  &.mobile-expanded {
    margin-top: ${rem(16)};
    margin-bottom: ${rem(16)};
  }
`

const HotelSearchBarFloating = styled.div`
  position: absolute;
  bottom: 0;
  transform: translateY(50%);
  width: 100%;
  z-index: ${zIndex.floatingPanel};

  &.floating-search-bar {
    position: relative;
    transform: unset;
    margin-top: ${rem(32)};
  }
`

interface Props {
  floatingSearchBar?: boolean
}

function HotelPageSearchHero({ floatingSearchBar, trendingDestinations, isSearchEnabled, filters }: Props & MappedStateProps) {
  const trendingDestinationsLabels = useTrendingDestinationsLabels(trendingDestinations)

  const naturalLanguageSearch = !!useOptimizelyExperiment(OptimizelyExperiments.searchNaturalLanguageSearch)
  const isMobileDevice = useIsMobileDevice()
  const hotelSearchExperimentVariation = useOptimizelyExperiment(
    OptimizelyExperiments.croHotelSearchDirectlyFromVerticalPageMobileWeb,
    isMobileDevice,
  )
  const isHotelSearchDirectlyOnPageVariant = getOptimizelyEnabledDecision(hotelSearchExperimentVariation ?? null)

  const globalSearchState = useContext(GlobalSearchStateContext)
  const { search } = useLocation()
  const dispatch = useAppDispatch()
  const formRef = useRef<HTMLFormElement>(null)

  const onSearch = () => {
    if (formRef.current?.reportValidity()) {
      const {
        searchItem,
        occupancies,
        checkinDate,
        checkoutDate,
        flexibleNights,
        isFlexibleDateSelected,
        flexibleMonths,
        userSelectedFlexibleMonths,
      } = globalSearchState

      const dates = convertDatesToString(ISO_DATE_FORMAT, checkinDate, checkoutDate)
      const queryParams = encodeSearchParams({
        urlSearch: search,
        searchItem,
        filters,
        dates,
        rooms: occupancies,
        isFlexibleWithDate: isFlexibleDateSelected,
        flexibleNights,
        flexibleMonths,
        userSelectedFlexibleMonths,
      }).toString()

      dispatch(pushWithRegion(`/search?${queryParams}`))
    }
  }

  const onSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    onSearch()
  }
  return <>
    {isSearchEnabled && <HeroContainer
      className={clsx({ 'expanded-mobile-search': isHotelSearchDirectlyOnPageVariant })}
      gravity="auto"
      aria-hidden="true"
      brightness={-25}
      id={config.searchFormSection.hotelsImageId}
      tabletAspectRatio="16:3"
    >
      <LayoutContainer relative>
        <Group direction="vertical" horizontalAlign="start" tabletHorizontalAlign="center" gap={8}>
          <Heading colour="neutral-eight" variant="heading2" className="hero-heading center">
            <i>Hotels & Resorts</i> to dream about
          </Heading>
          <BodyText variant="large" colour="neutral-eight" className="center">
            Choose from our curated hotels & resorts
          </BodyText>
        </Group>
      </LayoutContainer>

      <HotelSearchBarFloating className={clsx({ 'floating-search-bar': floatingSearchBar })}>
        <LayoutContainer>
          <CSSBreakpoint min="tablet">
            <HotelSearchContainerPaneContent>
              <SearchForm
                submitOnApply
                typeaheadTypes={HOTEL_SEARCH_TYPEAHEAD_TYPES}
                locationSearchPlaceholder={naturalLanguageSearch ? LOCATION_SEARCH_INPUT_PLACEHOLDER_LABEL_NATURAL_LANGUAGE : LOCATION_SEARCH_INPUT_PLACEHOLDER_LABEL}
                label={naturalLanguageSearch ? LOCATION_SEARCH_INPUT_TITLE_LABEL_NATURAL_LANGUAGE : LOCATION_SEARCH_INPUT_TITLE_LABEL}
                showNaturalLanguageSearchSuggestions={naturalLanguageSearch}
              />
            </HotelSearchContainerPaneContent>
          </CSSBreakpoint>

          <CSSBreakpoint max="mobile">
            {isHotelSearchDirectlyOnPageVariant && <HotelSearchContainerPaneContent className="mobile-expanded">
              <VerticalSpacer as="form" onSubmit={onSearchSubmit} gap={12} ref={formRef}>
                <HotelsTabContent isInPage onSearch={onSearch} />
                <TextButton fit="mobile-full-width" kind="primary" type="submit" size="large">Search</TextButton>
              </VerticalSpacer>
            </HotelSearchContainerPaneContent>}
            {!isHotelSearchDirectlyOnPageVariant && <GlobalMobileSearchHeroBar
              placeholder="Search hotels in"
              placeholderTextList={trendingDestinationsLabels}
              showAIGlowBorder={naturalLanguageSearch}
            />}
          </CSSBreakpoint>
        </LayoutContainer>
      </HotelSearchBarFloating>
    </HeroContainer>}
  </>
}

interface MappedStateProps {
  trendingDestinations: Array<App.TrendingDestination>
  isSearchEnabled: boolean
  filters: App.OfferListFilters
}

const mapStateToProps = (state: App.State): MappedStateProps => ({
  trendingDestinations: state.destination.trendingDestinations,
  isSearchEnabled: isSearchEnabled(),
  filters: getUrlOfferListFilters(state),
})

export default connect(mapStateToProps)(HotelPageSearchHero)
